import { DatePickerField, Input, Modal } from '@profitowi/component-library';
import { AxiosError, AxiosResponse } from 'axios';
import { endOfDay, format, parse } from 'date-fns';
import { Form, Formik } from 'formik';
import SchemaPropagationAlerts from 'pages/Records/components/SalesStructure/components/SchemaPropagationFields/SchemaPropagationAlerts';
import SchemaPropagationFields, {
  ActionLabelOptions,
  getSchemaPropagationPayload,
} from 'pages/Records/components/SalesStructure/components/SchemaPropagationFields/SchemaPropagationFields';
import { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';

import { ModalActions } from 'components';
import ErrorMessages from 'components/ErrorMessages/ErrorMessages';
import { requiredFieldMessage } from 'constants/requiredFieldMessage';
import { getAgentName } from 'helpers/agents';
import useDictionaryContext from 'hooks/useDictionaryContext';
import {
  ChangeSubordinationPayload,
  PropagationOptions,
  changeSubordinationPeriod,
} from 'services/structure';
import { AgentRelationStructureRecord } from 'types/structure';

const validationSchema = yup.object({
  validFrom: yup.date().required(requiredFieldMessage),
  validTo: yup.date().nullable(),
  propagateToSelectedSchemas: yup.string().required(requiredFieldMessage),
  selectedCommissionSchemaIds: yup.array().of(yup.string()).optional(),
});

type FormValues = yup.InferType<typeof validationSchema>;

type Props = {
  refetch: () => void;
};

const ChangeSubordinationPeriodModal = ({ refetch }: Props) => {
  const [validationWarnings, setValidationWarnings] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [payload, setPayload] = useState<ChangeSubordinationPayload>();
  const { translate } = useDictionaryContext();
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    if (!state) {
      navigate('..');
      refetch();
    }
  }, [navigate, state]);

  const {
    child,
    commissionSchema,
    id: relationId,
    parent,
    validFrom,
    validTo,
  } = (state || {}) as AgentRelationStructureRecord;

  const initialValues = useMemo(
    () => ({
      validFrom: parse(validFrom, 'dd-MM-yyyy HH:mm:ss', new Date()),
      validTo: validTo ? parse(validTo, 'dd-MM-yyyy HH:mm:ss', new Date()) : null,
      propagateToSelectedSchemas: PropagationOptions.PROPAGATE_TO_ALL_SCHEMAS_IN_GROUP,
      selectedCommissionSchemaIds: [],
    }),
    [validFrom, validTo]
  );

  const childName = useMemo(() => getAgentName(child?.economyActivities), [child]);
  const parentName = useMemo(() => getAgentName(parent?.economyActivities), [parent]);

  const { isLoading, isError, error, mutate } = useMutation<
    AxiosResponse,
    AxiosError,
    ChangeSubordinationPayload
  >((payload) => changeSubordinationPeriod(payload), {
    onSuccess: () => {
      navigate('..');
      refetch();
    },
  });

  const {
    isLoading: validationIsloading,
    isError: validationIsError,
    error: validationError,
    mutate: validateForm,
  } = useMutation<AxiosResponse, AxiosError, ChangeSubordinationPayload>(
    (payload) => changeSubordinationPeriod(payload, true),
    {
      onSuccess: (response) => {
        response.data.warnings && setValidationWarnings(response.data.warnings);
        response.data.errors && setValidationErrors(response.data.errors);

        const canPropagateSchema =
          !response.data.warnings?.length && !response.data.errors?.length && payload;

        if (canPropagateSchema) {
          mutate(payload);
        }
      },
    }
  );

  const handleSubmit = (values: FormValues) => {
    const propagationOptions = getSchemaPropagationPayload({
      commissionSchemaId: commissionSchema,
      ...values,
    });

    const payload = {
      relationId,
      validFrom: format(values.validFrom, 'dd-MM-yyyy 00:00:00'),
      validTo: values.validTo ? format(endOfDay(values.validTo), 'dd-MM-yyyy HH:mm:ss') : null,
      ...propagationOptions,
    };
    setPayload(payload);

    if (!validationErrors?.length && !!validationWarnings?.length && payload) {
      mutate(payload);
    } else {
      validateForm(payload);
    }
  };

  function onChange() {
    setValidationErrors([]);
    setValidationWarnings([]);
  }

  return (
    <Modal title="Edytuj daty podrzędności" visible>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={handleSubmit}>
        {({ values: { validFrom, validTo } }) => (
          <Form className="space-y-4" onBlur={onChange}>
            <Input
              label="Schemat prowizyjny"
              isReadOnly
              value={translate('COMMISSION_SCHEMA_NAME', String(commissionSchema))}
            />
            <Input label="Kontrahent" isReadOnly value={childName} />
            <Input label="Nadrzędny" isReadOnly value={parentName} />
            <DatePickerField label="Data od" name="validFrom" maxDate={validTo} />
            <DatePickerField label="Data do" name="validTo" minDate={validFrom} />
            {(isError || validationIsError) && <ErrorMessages error={error || validationError} />}

            <SchemaPropagationFields
              commissionSchemaId={commissionSchema}
              isLoading={isLoading || validationIsloading}
              actionLabel={ActionLabelOptions.CHANGE}
            />

            <SchemaPropagationAlerts
              validationWarnings={validationWarnings}
              validationErrors={validationErrors}
            />
            <ModalActions
              onCancel={() => navigate('..')}
              disableSubmit={!!validationErrors?.length}
              confirmLabel={
                !validationErrors?.length && !!validationWarnings?.length
                  ? 'Zapisz pomimo ostrzeżeń'
                  : 'Zapisz'
              }
              isLoading={isLoading || validationIsloading}
            />
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default ChangeSubordinationPeriodModal;
